import React, { useCallback, useRef, useEffect, useState } from "react";
import "./NurseList.css";
import { useTranslation } from "react-i18next";
import Widgets from "../../../../widgets";
import Icons from "../../../../Assets/Icons";
import { scrollToTop } from "../../../../modules/scrollToTop/scrollAction";
import { getAllNurse, postNurse, bulkUploadNurse } from "../../../../modules/NursignCare/ManageNurse/ManageNurseThunk";
import strings from "../../../../utils/strings";
import environment from "../../../../utils/environment";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import ToastPopup from "../../../../widgets/Toast/toastPopup";
import { getLocalizedText } from "../../../../utils/localization";

const NurseList = () => {
  const { t } = useTranslation();
  const breadcrumbs = ["nursing_care", "manage_nurse"];
  const paths = ["/manage-resource/nurse-list", "/manage-resource/nurse-list"];
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const statuses = queryParams.getAll("status");
  const [showFilter, setShowFilter] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState(statuses.length > 0 ? statuses : ["ACTIVE", "INACTIVE"]);
  const modalRef = useRef(null);
  const { i18n } = useTranslation();

  const normalizeLang = (lng) => {
    const base = (lng || "").toLowerCase();
    if (base.startsWith("en")) return "en";
    if (base.startsWith("vi") || base.startsWith("vn")) return "vi";
    return base;
  };
  const extractString = (val) => {
    if (val && typeof val === "object" && !Array.isArray(val)) {
      const lang = normalizeLang(i18n.language);
      return val[lang] || (lang === "vi" ? val.vi || val.vn : "") || val.en || val.vi || Object.values(val)[0] || "";
    }
    return val || "";
  };
  const getGenderText = (val) => {
    if (val && typeof val === "object" && !Array.isArray(val)) {
      return extractString(val) || "--";
    }
    const g = (val || "").toString().toLowerCase().trim();
    if (g === "male") return t("male");
    if (g === "female") return t("female");
    return val || "--";
  };
  const [currentPage, setCurrentPage] = useState(1); 
  const itemsPerPage = 10; 
  const dispatch = useDispatch();
  const statusTypes = ["ACTIVE", "INACTIVE"];
  const [totalPages, setTotalPages] = useState(1); 
  const [nurses, setNurses] = useState([]);
  const topRef = useRef(null);
  const navigate = useNavigate();
  const [showBulkInfo, setShowBulkInfo] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchNurses = async () => {
      try {
        const statusParams = {};
        if (selectedStatuses?.length === 1) {
          if (selectedStatuses.includes("ACTIVE")) statusParams.isActive = true;
          else if (selectedStatuses.includes("INACTIVE")) statusParams.isActive = false;
        }

        if (searchTerm) {
          // Fetch all nurses (no server filter) and filter client-side by name or ID
          const data = await dispatch(getAllNurse({ page: 1, limit: 1000, search: "", silent: true, ...statusParams })).unwrap();
          if (data?.status === "success") {
            const q = searchTerm.toLowerCase();
            const filtered = (data?.data?.nurses || []).filter((n) => {
              const fn = (extractString(n.firstName) || "").toLowerCase();
              const ln = (extractString(n.lastName) || "").toLowerCase();
              const empId = (extractString(n.employeeID) || "").toLowerCase();
              return fn.includes(q) || ln.includes(q) || `${fn} ${ln}`.trim().includes(q) || empId.includes(q);
            });
            setNurses(filtered);
            setTotalPages(1);
          } else {
            setNurses([]);
            setTotalPages(0);
          }
          return;
        }

        // Normal paginated fetch
        const data = await dispatch(getAllNurse({ page: currentPage, limit: itemsPerPage, search: "", ...statusParams })).unwrap();
        if (data?.status === "success") {
          setNurses(data?.data?.nurses || []);
          setTotalPages(data?.data?.totalPages || 0);
        } else {
          setNurses([]);
          setTotalPages(0);
        }
      } catch (error) {
        console.error("Failed to fetch nurses:", error);
      }
    };

    fetchNurses();
  }, [dispatch, currentPage, selectedStatuses, searchTerm]);

  const handleBulkUploadClick = () => {
    setShowBulkInfo(true);
  };

  const handleFileUpload = async (file) => {
    try {
      const result = await dispatch(bulkUploadNurse(file)).unwrap();
      if (result.status === "success") {
        const { successCount, failedCount, errors } = result.data || {};

        const filteredErrors = errors?.filter(err => err.row !== 1) || [];

        const duplicates = filteredErrors.filter(err => /exists|already|Trùng lặp|tồn tại/i.test(err.error));
        const otherErrors = filteredErrors.filter(err => !/exists|already|Trùng lặp|tồn tại/i.test(err.error));

        if (successCount > 0) ToastPopup.success(t("bulkUploadSuccess", { count: successCount }));

        if (duplicates.length > 0) ToastPopup.info(t("bulkUploadDuplicates", { count: duplicates.length }));

        if (otherErrors.length > 0) ToastPopup.error(t("bulkUploadErrors", { count: otherErrors.length }));

        if (successCount === 0 && failedCount === 0 && duplicates.length === 0 && otherErrors.length === 0) ToastPopup.info(t("bulkUploadCompleted"));

        setShowBulkInfo(false);

        const refreshData = await dispatch(
          getAllNurse({
            page: 1,
            limit: itemsPerPage,
            search: "",
          }),
        ).unwrap();

        if (refreshData?.status === "success") {
          setNurses(refreshData?.data?.nurses || []);
          setTotalPages(refreshData?.data?.totalPages || 0);
          setCurrentPage(1);
        }
      } else ToastPopup.error(result.message || t("bulkUploadFailed"));
    } catch (error) {
      console.error("Bulk upload failed:", error);
      ToastPopup.error(typeof error === "string" ? error : error?.message || t("bulkUploadFailedGeneric"));
    }
  };

  const scrollToTopLocal = () => {
    if (topRef.current) {
      topRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };
  useEffect(() => {
    scrollToTopLocal();
  }, []);
  const handleSearch = useCallback((value) => {
    setSearchTerm((value || "").trim());
    setCurrentPage(1);
  }, []);

  const handleStatusChange = (newStatuses) => {
    setSelectedStatuses(newStatuses);
    setCurrentPage(1); 
  };

  const handleClick = (pageNumber) => {
    scrollToTopLocal();
    setCurrentPage(pageNumber);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowFilter(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [modalRef]);

  const handleVerifyClick = (nurseId) => {
    
    navigate(`/manage-resource/get-nurse/${nurseId}`);
  };

  const statusIcons = {
    ACTIVE: Icons.Check,
    INACTIVE: Icons.CircleCross,
  };

  const handlePrevNext = (direction) => {
    scrollToTopLocal();
    if (direction === "prev" && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    } else if (direction === "next" && currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const renderPagination = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) {
        pages.push("...");
      }
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) {
        pages.push("...");
      }
      pages.push(totalPages);
    }
    return pages;
  };
  const addNurseButton = () => {
    navigate(strings.navigation.createNurse);
  };

  return (
    <div className="mr-container nurse-container" ref={topRef}>
      <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />
      <div className="manage-service-heading department-heading">{t("manageNurse")}</div>
      <div className="nurse-search-line">
        <Widgets.SearchBox onSearch={handleSearch} placeholder={t("searchbynursename")} />
        <div className="nurse-add-filter">
          <Widgets.IconButton iconSrc={Icons.publish} onClick={handleBulkUploadClick} buttonText={t("bulkUpload")} />
          <Widgets.IconButton iconSrc={Icons.plusIconGreen} onClick={addNurseButton} buttonText={t("addNurseProfile")} />

          <div className="ppl-icon-container" ref={modalRef}>
            <section className="ppl-icon-filter" onClick={() => setShowFilter(!showFilter)}>
              <img src={Icons.filterIcon} alt="icon-filter" />
            </section>
            {showFilter && (
              <section className="ppl-status-dropdown">
                <Widgets.StatusDropdown
                  statusTypes={statusTypes}
                  onStatusChange={handleStatusChange}
                  setShowFilter={setShowFilter}
                  selectedStatuses={selectedStatuses}
                  setSelectedStatuses={setSelectedStatuses}
                />
              </section>
            )}
          </div>
        </div>
      </div>
      <div className="nurse-list-container">
        <div className="nurse-table-body">
          <table className="nurse-table">
            <thead className="nurse-thead">
              <tr>
                <th>{t("homecareStaffDetails")}</th>
                <th>{t("employeeId")}</th>
                <th>{t("gender")}</th>
                <th>{t("jobTitle")}</th>
                <th>{t("phoneNumber")}</th>
                <th>{t("emailId")}</th>
                <th>{t("serviceGroupItems")}</th>
                <th>{t("status")}</th>
              </tr>
            </thead>
            <tbody className="ppl-tbody">
              {nurses.length > 0 ? (
                nurses.map((item, index) => (
                  <tr key={index} className="ppl-table-row" onClick={() => handleVerifyClick(item._id)}>
                    <td>
                      <div className="ppl-td-first">
                        <div>
                          {item.profilePhoto ? (
                            <img src={item.profilePhoto.startsWith("http") ? item.profilePhoto : `${environment.apiURL}${item.profilePhoto}`} alt="profile-pic" className="ppl-td-profile" />
                          ) : (
                            <div className="ppl-detials">
                              <img src={Icons.profile} alt="profile-pic" className="ppl-td-profile-none" />
                            </div>
                          )}
                        </div>
                        <div className="ppl-name-email">
                          <section>
                            {extractString(item.firstName)} {extractString(item.lastName)}
                          </section>
                        </div>
                      </div>
                    </td>
                    <td>{(extractString(item.employeeID) || "--").toString().toUpperCase()}</td>
                    <td>{getGenderText(item.gender)}</td>
                    <td>{extractString(item.designation) || "--"}</td>
                    <td className="nowrap-cell">{item.phoneNumber ? `${item.countryCode ? `+${item.countryCode} ` : ""}${item.phoneNumber}` : "--"}</td>
                    <td>{item.email || "--"}</td>
                    <td>{Array.isArray(item.specialization) ? item.specialization.map((s) => extractString(s.title || s)).join(", ") : "--"}</td>
                    {/* <td className={`ppl-status ${item.isActive ? "ACTIVE" : "INACTIVE"}`}>
                      <p>
                        <img src={statusIcons[item.isActive ? "ACTIVE" : "INACTIVE"]} alt="status" />
                        {item.isActive ? t("Active") : t("Inactive")}
                      </p>
                    </td> */}
                    <td className={`ppl-status ${item.isActive ? "active-status" : "inactive-status"}`}>
                      <p>
                        <img src={statusIcons[item.isActive ? "ACTIVE" : "INACTIVE"]} alt="status" />
                        {item.isActive ? t("active") : t("inactive")}
                      </p>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8">
                    <div className="no-record-container ppl">
                      <div className="no-record-icon">
                        <img src={Icons.NoData} alt="No Data Found" />
                      </div>
                      <div className="no-record-text">{t("noRecordFound")}</div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="nurse-pagination-list">
            {totalPages > 1 && (
              <>
                <button onClick={() => handlePrevNext("prev")} disabled={currentPage === 1} className="nurse-pagination-prev-list">
                  <img src={Icons.leftArrow} alt="<" />
                </button>

                {renderPagination().map((page, index) => (
                  <button
                    key={index}
                    onClick={() => typeof page === "number" && handleClick(page)}
                    disabled={page === currentPage || page === "..."}
                    className={`nurse-pagination-btn-list ${page === currentPage ? "active-list" : ""} ${page === "..." ? "ellipse" : ""}`}
                  >
                    {typeof page === "number" ? page : <img src={Icons.extraIcon} alt="..." className="nurse-extra-icon" />}
                  </button>
                ))}

                <button onClick={() => handlePrevNext("next")} disabled={currentPage === totalPages} className="nurse-pagination-next-list">
                  <img src={Icons.rightArrow} alt=">" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      <Widgets.BulkUploadInfoModal
        showModal={showBulkInfo}
        onClose={() => setShowBulkInfo(false)}
        onAlpha2Click={() => {
          setShowBulkInfo(false);
          window.open(`#${strings.navigation.alpha2codeList}`, "_blank");
        }}
        onUpload={handleFileUpload}
      />
    </div>
  );
};

export default NurseList;
